import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy | Blanco Pro Services" },
      { name: "description", content: "How Blanco Pro Services LLC collects, uses, and protects your personal information, including SMS opt-in data." },
      { property: "og:title", content: "Privacy Policy | Blanco Pro Services" },
      { property: "og:description", content: "How Blanco Pro Services LLC collects, uses, and protects your personal information, including SMS opt-in data." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://blancopros.com/privacy" }],
  }),
  component: PrivacyPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="font-display text-2xl md:text-3xl font-bold mb-4 text-foreground">{title}</h2>
      <div className="space-y-4 text-foreground/80 leading-relaxed">{children}</div>
    </section>
  );
}

function Sub({ title }: { title: string }) {
  return <h3 className="font-display font-bold text-lg mt-6">{title}</h3>;
}

function List({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-6 space-y-2">
      {items.map((i) => (
        <li key={i}>{i}</li>
      ))}
    </ul>
  );
}

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <header className="mb-14 border-b border-foreground/10 pb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3 font-display">Legal</p>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] mb-4">Privacy Policy</h1>
          <p className="text-foreground/60 text-sm">Blanco Pro Services LLC · Effective Date: July 30, 2026</p>
        </header>

        <div className="rounded-2xl border border-gold/30 bg-gold/[0.06] p-6 mb-12 text-foreground/85 leading-relaxed">
          Blanco Pro Services LLC ("Company," "we," "our," or "us") DOES NOT share customer opt-in information, including phone numbers and consent records, with any affiliates or third parties for marketing, promotional, or any other purposes unrelated to providing our direct services. All text messaging originator opt-in data is kept strictly confidential.
        </div>

        <Section title="1. Information We Collect">
          <p>We collect the following types of information:</p>
          <Sub title="Personal Information" />
          <List
            items={[
              "Name",
              "Email address",
              "Phone number",
              "Physical address",
              "Payment information when you make a purchase or request a quote",
              "Opt-in records and timestamps for all communication channels (SMS, email, etc.)",
            ]}
          />
          <Sub title="Non-Personal Information" />
          <List
            items={[
              "IP address",
              "Browser type",
              "Device information",
              "Website usage patterns and analytics",
              "Cookies and similar tracking technologies",
            ]}
          />
          <Sub title="Customer Communication" />
          <List
            items={[
              "Records of inquiries and service requests",
              "Appointment details and preferences",
              "Service history and feedback",
            ]}
          />
        </Section>

        <Section title="2. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <List
            items={[
              "Provide and improve our services",
              "Process transactions and payments",
              "Communicate with you regarding your inquiries, appointments, customer support, and promotions",
              "Enhance website functionality and user experience",
              "Ensure security and help prevent fraud",
              "Maintain records of your communication preferences and consent",
            ]}
          />
        </Section>

        <Section title="3. SMS Messaging & Compliance">
          <Sub title="Text Message Program Terms & Conditions" />
          <p>
            By opting into our SMS messaging services, you agree to receive text messages related to our services, including appointment reminders, customer support, service updates, and other important notifications.
          </p>

          <Sub title="Opt-In & Consent" />
          <List
            items={[
              "You will only receive SMS messages if you have explicitly opted in.",
              "We maintain timestamped records of all opt-in actions.",
              "We comply with the Telephone Consumer Protection Act (TCPA), the CTIA Messaging Principles, and all applicable federal and state laws.",
            ]}
          />

          <Sub title="Opt-Out Instructions" />
          <List
            items={[
              "You may cancel SMS notifications at any time by replying STOP.",
              "You will receive a final confirmation message, and no further messages will be sent unless you opt in again.",
              "All opt-out requests are processed promptly.",
            ]}
          />

          <Sub title="Message Frequency & Content" />
          <List
            items={[
              "Message frequency varies based on your interactions with our business.",
              "Messages will relate only to the services you have requested.",
              "Promotional text messages are sent only with your separate consent.",
            ]}
          />

          <Sub title="Help & Support" />
          <p>Reply HELP for assistance or contact us:</p>
          <p>
            Email:{" "}
            <a className="text-gold hover:underline" href="mailto:sevice@blancopros.com">sevice@blancopros.com</a>
            <br />
            Phone: <a className="text-gold hover:underline" href="tel:+19088956133">+1 (908) 895-6133</a>
          </p>
          <p>Customer support is available during regular business hours.</p>

          <Sub title="Carrier Information" />
          <List
            items={[
              "Standard message and data rates may apply.",
              "Carriers are not responsible for delayed or undelivered messages.",
              "Supported carriers include AT&T, Verizon, T-Mobile, Sprint, and most regional carriers.",
            ]}
          />

          <Sub title="SMS Data Protection Statement" />
          <List
            items={[
              "No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.",
              "Information sharing with subcontractors that provide support services, such as customer service or message delivery, is permitted only as necessary to provide those services.",
              "All other use case categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.",
              "We implement strict safeguards to protect SMS consent records and opt-in information.",
            ]}
          />
        </Section>

        <Section title="4. Information Sharing & Disclosure">
          <p>We do not sell, rent, or trade your personal information. We may share information only in the following circumstances:</p>

          <Sub title="Service Providers" />
          <List
            items={[
              "Third-party vendors that assist with payment processing, appointment scheduling, website hosting, or customer support",
              "SMS aggregators and messaging providers solely for delivering messages you have consented to receive",
              "All service providers are contractually required to maintain confidentiality and security",
            ]}
          />

          <Sub title="Legal Compliance" />
          <p>We may disclose information if required by law, legal process, court order, or to protect our legal rights.</p>

          <Sub title="Business Transfers" />
          <p>
            If Blanco Pro Services LLC is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction while remaining protected under this Privacy Policy.
          </p>
          <p>
            All of the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties except aggregators and providers necessary to deliver SMS services.
          </p>
        </Section>

        <Section title="5. Data Security">
          <p>We maintain reasonable administrative, technical, and physical safeguards to protect your information, including:</p>
          <List
            items={[
              "Encryption of sensitive information during transmission and storage",
              "Secure authentication and access controls",
              "Routine security assessments and updates",
              "Employee training on data protection",
              "Breach notification procedures where required by law",
              "Secure backup and disaster recovery practices",
            ]}
          />
          <p>While we use commercially reasonable measures to protect your information, no method of electronic transmission or storage is completely secure.</p>
        </Section>

        <Section title="6. Cookies & Tracking Technologies">
          <p>We use cookies and similar technologies to:</p>
          <List
            items={[
              "Analyze website traffic and visitor behavior",
              "Remember user preferences",
              "Improve website functionality",
              "Measure the effectiveness of our services",
            ]}
          />
          <p>You may disable cookies through your browser settings, although doing so may affect certain website features.</p>
        </Section>

        <Section title="7. Your Rights & Choices">
          <p>You have the right to:</p>
          <List
            items={[
              "Access, update, or request deletion of your personal information",
              "Unsubscribe from marketing emails using the unsubscribe link",
              "Opt out of SMS communications by replying STOP",
              "Request information regarding how your data is processed",
              "Withdraw consent for future communications",
              "Submit a complaint to an appropriate regulatory authority if you believe your rights have been violated",
            ]}
          />
          <p>To exercise any of these rights, please contact us using the information below.</p>
        </Section>

        <Section title="8. Third-Party Links">
          <p>Our website may contain links to third-party websites. We are not responsible for the privacy practices of those websites.</p>
          <p>This Privacy Policy applies only to information collected by Blanco Pro Services LLC.</p>
        </Section>

        <Section title="9. Changes to This Privacy Policy">
          <p>We may update this Privacy Policy from time to time.</p>
          <p>
            The latest version will always be posted on our website with the updated effective date. If significant changes are made, we may notify you by email or through a notice posted on our website.
          </p>
        </Section>

        <Section title="10. Contact Us">
          <p>If you have any questions about this Privacy Policy or our privacy practices, please contact us:</p>
          <div className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-6 mt-4 space-y-2">
            <p className="font-display font-bold text-foreground">Blanco Pro Services LLC</p>
            <p className="text-foreground/80">
              119 Lincoln Street<br />
              South Bound Brook, NJ 08880
            </p>
            <p>
              <strong>Phone:</strong>{" "}
              <a className="text-gold hover:underline" href="tel:+19088956133">+1 (908) 895-6133</a>
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a className="text-gold hover:underline" href="mailto:sevice@blancopros.com">sevice@blancopros.com</a>
            </p>
            <p>
              <strong>Website:</strong>{" "}
              <a className="text-gold hover:underline" href="https://www.blancopros.com">https://www.blancopros.com</a>
            </p>
          </div>
          <p className="mt-6">By using our website and services, you acknowledge that you have read and agree to this Privacy Policy.</p>
        </Section>
      </div>
    </div>
  );
}
